'use client';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { getT, type Lang } from './i18n';

export const LANG_STORAGE_KEY = 'logixa_lang';

/**
 * Resolve the current UI language for the visitor:
 *   1. logged-in user.language (set in Settings)
 *   2. localStorage `logixa_lang` (chosen via LanguageSwitcher while anonymous)
 *   3. browser navigator.language (best-effort initial guess)
 *   4. 'DE' as the platform's primary language
 *
 * Hooked component re-renders when localStorage changes (storage event)
 * so the homepage live-updates after a switcher click without a full
 * reload.
 */
export function useT() {
  const user = useAuthStore(s => s.user);
  const [storageLang, setStorageLang] = useState<string | null>(null);

  useEffect(() => {
    try {
      setStorageLang(localStorage.getItem(LANG_STORAGE_KEY));
    } catch {/* private mode */}
    const onStorage = (e: StorageEvent) => {
      if (e.key === LANG_STORAGE_KEY) setStorageLang(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    // Custom event so a switcher in the SAME tab also triggers re-render
    // (storage events only fire across tabs).
    const onLocal = () => {
      try { setStorageLang(localStorage.getItem(LANG_STORAGE_KEY)); } catch {}
    };
    window.addEventListener('logixa-lang-change', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('logixa-lang-change', onLocal);
    };
  }, []);

  const browserGuess = typeof navigator !== 'undefined'
    ? guessFromBrowser(navigator.language)
    : null;

  const lang: Lang = (user?.language as Lang)
    || (storageLang as Lang)
    || (browserGuess as Lang)
    || 'DE';

  return getT(lang);
}

/**
 * Same resolution chain as useT() but returns the active 2-letter API
 * language code (de/en/ru/ua/md/kz/cz/pl/bg) instead of the t() function.
 * Use it when you need to pass `?lang=` to a public API endpoint that
 * returns localized data.
 */
export function useApiLang(): string {
  const user = useAuthStore(s => s.user);
  const [storageLang, setStorageLang] = useState<string | null>(null);

  useEffect(() => {
    try { setStorageLang(localStorage.getItem(LANG_STORAGE_KEY)); } catch { /* private mode */ }
    const onStorage = (e: StorageEvent) => {
      if (e.key === LANG_STORAGE_KEY) setStorageLang(e.newValue);
    };
    const onLocal = () => {
      try { setStorageLang(localStorage.getItem(LANG_STORAGE_KEY)); } catch {}
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('logixa-lang-change', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('logixa-lang-change', onLocal);
    };
  }, []);

  const browserGuess = typeof navigator !== 'undefined'
    ? guessFromBrowser(navigator.language)
    : null;
  const raw: string = (user?.language as string)
    || (storageLang as string)
    || (browserGuess as string)
    || 'DE';
  // Map UI codes (UPPER) to API 2-letter (lower) — UI uses UA/MD/KZ/CZ which
  // already match the API. Future-proof if locale codes diverge.
  const lc = raw.toLowerCase();
  const map: Record<string, string> = {
    de: 'de', en: 'en', ru: 'ru', ua: 'ua', uk: 'ua', md: 'md', ro: 'md',
    kz: 'kz', kk: 'kz', cz: 'cz', cs: 'cz', pl: 'pl', bg: 'bg',
  };
  return map[lc] || 'de';
}

/** Map navigator.language ("en-US", "de", "ru-RU") to our 9-lang codes. */
function guessFromBrowser(navLang: string): Lang | null {
  const code = navLang.slice(0, 2).toLowerCase();
  const map: Record<string, Lang> = {
    en: 'EN', de: 'DE', ru: 'RU', uk: 'UA',
    pl: 'PL', cs: 'CZ', ro: 'MD', mo: 'MD',
    bg: 'BG', kk: 'KZ', kz: 'KZ',
  };
  return map[code] || null;
}
